import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getDashboardStats, getRTDashboardStats, getDashboardStatsByGroup, getRTDashboardStatsByGroup, getSiteAnalytics } from "../../Api Methods/Api";
import { useDispatch, useSelector } from "react-redux";
import { Doughnut, Bar } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";
import useSetCookie from "../../components/hooks/UseSetCookie";
import { serviceActions } from "../../store/service-Slice";
import StatCard from "../../components/UI/Stats/StatCard";
import classes from './Dashboard.module.css';
import mixpanel from "mixpanel-browser";
import CCTVAnalytics from "../CCTVIntegrations/CCTVAnalytics";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { FilterMatchMode } from "primereact/api";
import { DateRangePicker } from 'rsuite';
import ExportExcel from "../../components/ExcelExport/ExcelExport";
import { setDate } from "date-fns";

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart) {
        const cfg = chart.config.options.plugins.centerText;
        if (!cfg?.show) return;
        const { ctx, chartArea } = chart;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.font = `600 16px -apple-system, BlinkMacSystemFont, sans-serif`;
        ctx.fillStyle = "#5c5c5c";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(cfg.text, cx, cy);
        ctx.restore();
    },
};
ChartJS.register(centerTextPlugin);

const C = {
    critical: "#df7474",
    warning: "#efbc65",
    healthy: "#5fdc8d",
    online: "#60e08f",
    offline: "#ee6d6d",
    // offline: "#fec665",
    missed1poll: "#e8944f",
    powerDown: "#d95d5d",
    unknown: "#9ca3af",
    mains: "#6298ee",
    solar: "#b2e368",
    battery: "#f3b853",
    poor: "#d95d5d",
    fair: "#e8944f",
    good: "#5fdc8d",
};

const makeOptions = (centerText) => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
        legend: {
            position: "bottom",
            labels: {
                font: { size: 11 },
                padding: 10,
                boxWidth: 10,
                usePointStyle: true,
                generateLabels: (chart) => {
                    const { labels, datasets } = chart.data;
                    return labels.map((label, i) => ({
                        text: `${label} - ${datasets[0].data[i]?.toLocaleString()}`,
                        fillStyle: datasets[0].backgroundColor[i],
                        strokeStyle: datasets[0].backgroundColor[i],
                        pointStyle: "circle",
                        hidden: false,
                        index: i,
                    }));
                },
            },
        },
        tooltip: { enabled: true },
        centerText: { show: true, text: centerText },
    },
});

const DonutCard = ({ title, labels, values, colors, centerText }) => {
    const total = values.reduce((a, b) => a + b, 0);
    const data = {
        labels,
        datasets: [{ data: values, backgroundColor: colors, borderWidth: 0, hoverOffset: 4 }],
    };
    const displayText = centerText !== undefined ? centerText : total.toLocaleString();
    return (
        <div className={classes.chartCard}>
            <p className={classes.chartTitle}>{title}</p>
            <div className={classes.donutContainer}>
                <Doughnut data={data} options={makeOptions(displayText)} />
            </div>
        </div>
    );
};



const SkeletonCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonTop}>
            <div className={classes.skeletonBar} style={{ height: "18px", width: "60%" }} />
            <div className={classes.skeletonBar} style={{ height: "24px", width: "24px", borderRadius: "50%" }} />
        </div>
        <div className={classes.skeletonBar} style={{ height: "32px", width: "40%" }} />
    </div>
);

const SkeletonChart = () => (
    <div className={classes.chartCard}>
        <div className={classes.skeletonBar} style={{ height: "14px", width: "55%", marginBottom: "16px" }} />
        <div className={classes.skeletonCircle} />
        <div className={classes.skeletonBar} style={{ height: "10px", width: "80%", marginTop: "14px" }} />
    </div>
);

const USER_PORTAL_OPTIONS = ["All Users", "Exclude Portal Users"];
const USER_TIMES = ["7D", "1M", "3M"];
const USER_LEGEND_COLORS = ["#60e08f", "#6298ee", "#efbc65", "#df7474", "#75c71e", "#1eb9c7"];

const SkeletonBarChart = () => (
    <div className={classes.chartCard}>
        <div className={classes.skeletonBar} style={{ height: "14px", width: "40%", marginBottom: "16px" }} />
        <div className={classes.skeletonBar} style={{ height: "337px", width: "100%" }} />
    </div>
);

const SkeletonTableCard = () => (
    <div className={classes.chartCard}>
        <div className={classes.skeletonBar} style={{ height: "14px", width: "15%", marginBottom: "16px" }} />
        <div style={{ border: "1px solid rgba(0, 0, 0, 0.1)", borderRadius: "8px", padding: "8px" }}>
            {[...Array(5)].map((_, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "14px 8px", borderBottom: i < 4 ? "1px solid rgba(0,0,0,0.1)" : "none" }}>
                    <div className={classes.skeletonBar} style={{ height: "12px", width: "55%" }} />
                    <div className={classes.skeletonBar} style={{ height: "12px", width: "40px" }} />
                </div>
            ))}
        </div>
    </div>
);

const AnalyticsBarCard = ({ title, data, onItemClick, selectedStat }) => {
    const chartData = {
        labels: data.slice(0, 10).map(item => selectedStat === 0 ? (item.siteName || item.siteKey) : item.fullName),
        datasets: [{ label: "", data: data.slice(0, 10).map(item => selectedStat === 0 ? item.totalActivity : item.total_actions), backgroundColor: USER_LEGEND_COLORS, borderWidth: 0 }],
    };
    return (
        <div className={classes.chartCard}>
            <p className={classes.chartTitle}>{title}</p>
            <div className={classes.userLegend}>
                {data.slice(0, 10).map((item, i) => (
                    <div
                        key={selectedStat === 0 ? item.siteKey : item.fullName}
                        className={classes.userLegendItem}
                        onClick={() => {
                            if (selectedStat === 0) {
                                onItemClick(item.siteUUID);
                            } else if (item.userID != null) {
                                onItemClick(item.userID, item.fullName);
                            }
                        }}
                        style={{ cursor: (selectedStat === 0 || item.userID != null) ? 'pointer' : 'default' }}
                    >
                        <span className={classes.userLegendDot} style={{ backgroundColor: USER_LEGEND_COLORS[i % USER_LEGEND_COLORS.length] }} />
                        <span>{selectedStat === 0 ? (item.siteName || item.siteKey) : item.fullName}</span>
                    </div>
                ))}
            </div>
            <div style={{ height: "300px" }}>
                <Bar data={chartData} options={{ indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { enabled: true } }, scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } }, y: { grid: { display: false }, ticks: { autoSkip: false } } } }} />
            </div>
        </div>
    );
};

const AnalyticsTableCard = ({ title, data, onItemClick, selectedStat }) => (
    <div className={classes.chartCard}>
        <p className={classes.chartTitle}>{title}</p>
        {selectedStat === 0 ? (
            <DataTable value={data} scrollable scrollHeight='400px' stripedRows style={{ border: "1px solid rgba(0, 0, 0, 0.1)", borderRadius: "8px", padding: "8px" }}>
                <Column field="siteKey" header="Site ID" body={(row) => <Link className={classes.tableUser} onClick={() => onItemClick(row.siteUUID)} to={`/Dashboard/SiteView/${row.siteUUID}`}>{row.siteKey}</Link>} />
                <Column field="siteName" header="Name" body={(row) => row.siteName || "[No Name Set]"} />
                <Column field="totalActivity" header="Total Activity" sortable />
                <Column field="latestActivity" header="Latest Activity" sortable body={(row) => row.latestActivity ? new Date(row.latestActivity).toISOString().split('T')[0] : ''} />
            </DataTable>
        ) : (
            <DataTable value={data} scrollable scrollHeight='400px' stripedRows style={{ border: "1px solid rgba(0, 0, 0, 0.1)", borderRadius: "8px", padding: "8px" }}>
                <Column field="fullName" header="User" body={(row) => <span className={classes.tableUser} onClick={() => onItemClick(row.userID, row.fullName)}>{row.fullName}</span>} />
                <Column field="total_actions" header="Total Actions" />
            </DataTable>
        )}
    </div>
);

const AnalyticsSectionHeader = ({ titles, selectedTime, setSelectedTime, selectedStat, setSelectedStat, dateRange, setDateRange, handleOK, userAnalyticsTab, disableDates }) => (
    <div className={classes.sectionHeaderTime}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "18px" }}>
            {titles.map((tab, i) => (
                <p
                    key={tab}
                    className={classes.sectionTitle}
                    onClick={() => setSelectedStat(i)}
                    style={{
                        opacity: selectedStat === i ? 1 : 0.5,
                        borderBottom: selectedStat === i ? "3px solid #56AD9C" : "3px solid transparent",
                        paddingBottom: "4px",
                        cursor: "pointer",
                        transition: "opacity 0.1s, border-bottom 0.1s"
                    }}
                >
                    {tab}
                </p>
            ))}
        </div>
        <div className={classes.timeDropdown}>
            {userAnalyticsTab === 0 && (
                <DateRangePicker
                    style={{
                        display: "flex",
                        border: "1px solid #00000099",
                        borderRadius: "8px",
                        height: "30px",
                    }}
                    placement="bottomEnd"
                    format="yyyy.MM.dd"
                    value={dateRange ? [new Date(dateRange[0]), new Date(dateRange[1])] : null}
                    onOk={handleOK}
                    onClean={() => (
                        setDateRange(null),
                        setSelectedTime("7D")
                    )}
                    shouldDisableDate={disableDates}
                    appearance="subtle"
                    cleanable={true}
                    ranges={[]}
                />
            )}
            <div className={classes.timeButtons}>
                {USER_TIMES.map((t) => (
                    <button 
                        key={t} 
                        className={selectedTime === t ? classes.timeButtonActive : classes.timeButton} 
                        onClick={() => {
                            setSelectedTime(t)
                            setDateRange(null)
                        }}
                        >{t}</button>
                ))}
            </div>
        </div>
    </div>
);

const RTSectionHeader = ({ title, lastRTUpdate, secondsAgo, formatSecondsAgo }) => (
    <div className={classes.sectionHeader}>
        <p className={classes.sectionTitle}>{title}</p>
        {lastRTUpdate && (
            <span className={classes.lastUpdated}>Updated {formatSecondsAgo(secondsAgo)}</span>
        )}
    </div>
);



const Dashboard = () => {
    const navigate = useNavigate();
    const setCookie = useSetCookie();
    const [stats, setStats] = useState(null);
    const [RTStats, setRTStats] = useState(null);
    const [lastRTUpdate, setLastRTUpdate] = useState(null);
    const [secondsAgo, setSecondsAgo] = useState(0);
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const companyName = useSelector(state => state.auth.companyName);
    const firstName = useSelector(state => state.auth.firstName);
    const apiDataRef = useRef({ token, client, companyName });
    apiDataRef.current = { token, client, companyName };
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingRT, setIsLoadingRT] = useState(true);

    const [hasIons, setHasIons] = useState(false)
    const [hasLocks, setHasLocks] = useState(false)
    const [hasKeyboxes, setHasKeyboxes] = useState(false)
    const [hasGateways, setHasGateways] = useState(false)
    const [hasSensors, setHasSensors] = useState(false)
    const [hasCCTV, setHasCCTV] = useState(false)

    const [billableSensorCount, setBillableSensorCount] = useState(0)
    const [viewMode, setViewMode] = useState('portfolio'); // 'portfolio' | 'myGroup'

    const savedState = JSON.parse(sessionStorage.getItem('dashboardSiteState') || 'null');
    const isFirstMount = useRef(true);

    const [topUserStats, setTopUserStats] = useState([]);
    const [allUserStats, setAllUserStats] = useState([]);
    const [topSitesStats, setTopSitesStats] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [selectedTime, setSelectedTime] = useState(savedState ? savedState.selectedTime : "7D");
    const [portalFilter, setPortalFilter] = useState(savedState ? savedState.portalFilter : "All Users");
    const [dateRange, setDateRange] = useState(savedState ? savedState.dateRange : null);
    const [userAnalyticsTab, setUserAnalyticsTab] = useState(savedState ? savedState.userAnalyticsTab : 0); // 0 = top users, 1 = all users
    const [selectedStat, setSelectedStat] = useState(savedState ? savedState.selectedStat : 0);
    const [selectedStartDate, setSelectedStartDate] = useState(savedState?.selectedStartDate ? new Date(savedState.selectedStartDate) : null);
    const [selectedEndDate, setSelectedEndDate] = useState(savedState?.selectedEndDate ? new Date(savedState.selectedEndDate) : null);
    const [userFilters] = useState({
        fullName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [formattedExportData, setFormattedExportData] = useState([]);

    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    useEffect(() => {
        mixpanel.track("Dashboard visited", { user: localStorage.getItem("email") });
    }, []);

    useEffect(() => {
        if (savedState) {
            setTimeout(() => sessionStorage.removeItem('dashboardSiteState'), 0);
        }
    }, []);

    useEffect(() => {
        if (allUserStats && allUserStats.length > 0) {
            const formatted = allUserStats.map(user => ({
                "Name": user.fullName,
                "Email": user.email,
                "Site Count": user.totalSites,
                "Created": user.created,
                "Last Login": user.lastLoggedIn
            }))
        setFormattedExportData(formatted);
        }

    }, [allUserStats])

    useEffect(() => {
        console.log("apidata:", apiDataRef.current);
        console.log("companyName:", localStorage.getItem('companyName'));

        setStats(null);
        setRTStats(null);
        setIsLoading(true);
        setIsLoadingRT(true);

        const isGroup = viewMode === 'myGroup';
        const groupBody = { groupName: apiDataRef.current.companyName };

        const HandleGetStats = async () => {
            if (isGroup) {
                const response = await getDashboardStatsByGroup(apiDataRef.current, groupBody, dispatch);
                const data = response.data[0][0];
                setStats(data);
                console.log(data);
                setHasIons(data.controllerCount > 0);
                setHasLocks(data.lockCount > 0);
                setHasKeyboxes(data.keyboxCount > 0);
                setHasGateways(data.gatewayCount > 0);
                setHasSensors(data.sensorCount > 0);
                setHasCCTV(data.totalIntegrations > 0);
                setBillableSensorCount((data.billedBLESensorCount ?? 0) + (data.billedNBIOTSensorCount ?? 0));
            } else {
                const response = await getDashboardStats(apiDataRef.current, dispatch);
                const data = response.data[0][0];
                setStats(data);
                console.log(data);
                setHasIons(data.controllerCount > 0);
                setHasLocks(data.lockCount > 0);
                setHasKeyboxes(data.keyboxCount > 0);
                setHasGateways(data.gatewayCount > 0);
                setHasSensors(data.sensorCount > 0);
                setHasCCTV(data.totalIntegrations > 0);
                setBillableSensorCount(data.billedBLESensorCount + data.billedNBIOTSensorCount);
            }
            setIsLoading(false);
        };

        const HandleGetRTStats = async () => {
            if (isGroup) {
                const response = await getRTDashboardStatsByGroup(apiDataRef.current, groupBody, dispatch);
                setRTStats(response.data[0][0]);
                console.log(response.data[0][0]);
            } else {
                const response = await getRTDashboardStats(apiDataRef.current, dispatch);
                setRTStats(response.data[0][0]);
                console.log(response.data[0][0]);
            }
            setIsLoadingRT(false);
            setLastRTUpdate(Date.now());
        };

        HandleGetStats();
        HandleGetRTStats();

        const pollInterval = setInterval(HandleGetRTStats, 60000);
        return () => clearInterval(pollInterval);
    }, [viewMode]);

    useEffect(() => {
        if (!lastRTUpdate) return;
        setSecondsAgo(0);
        const tick = setInterval(() => {
            setSecondsAgo(Math.floor((Date.now() - lastRTUpdate) / 1000));
        }, 1000);
        return () => clearInterval(tick);
    }, [lastRTUpdate]);

    const formatSecondsAgo = (s) => {
        if (s < 60) return `${s}s ago`;
        return `${Math.floor(s / 60)}m ${s % 60}s ago`;
    };

    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setDateRange(null)
        setSelectedTime("7D")
    }, [selectedStat]);

    useEffect(() => {
        const savedState = sessionStorage.getItem('dashboardSiteState');
        if (savedState) return;

        if (userAnalyticsTab === 0) {
            setDateRange(null)
            setSelectedTime("7D")
        }
    }, [userAnalyticsTab]);

    useEffect(() => {
        if (selectedStat === 1 && userAnalyticsTab !== 0) return;

        const abortController = new AbortController();
        const now = new Date();
        const last7 = new Date(); last7.setDate(now.getDate() - 7);
        const last30 = new Date(); last30.setDate(now.getDate() - 30);
        const last90 = new Date(); last90.setDate(now.getDate() - 90);

        const fetchTopUsers = async () => {
            setIsLoadingUsers(true);
            try {
                const response = await getSiteAnalytics(apiDataRef.current, {
                    groupName: apiDataRef.current.companyName,
                    start_date: dateRange ? new Date(dateRange[0]) : (selectedTime === "7D" ? last7 : selectedTime === "1M" ? last30 : last90),
                    end_date: dateRange ? new Date(dateRange[1]) : now,
                    analyticsType: "users",
                    hasPortalUsers: portalFilter === "Exclude Portal Users" ? 1 : 0
                }, dispatch, abortController.signal);
                setTopUserStats(response.data?.[0]?.topUsers || []);
                setIsLoadingUsers(false);
            } catch (err) {
                if (err.name !== "AbortError") console.error(err);
            }
        };

        const fetchTopSites = async () => {
            setIsLoadingUsers(true);
            try {
                const response = await getSiteAnalytics(apiDataRef.current, {
                    groupName: apiDataRef.current.companyName,
                    start_date: dateRange ? new Date(dateRange[0]) : (selectedTime === "7D" ? last7 : selectedTime === "1M" ? last30 : last90),
                    end_date: dateRange ? new Date(dateRange[1]) : now,
                    analyticsType: "topSites",
                }, dispatch, abortController.signal);
                setTopSitesStats(response.data?.[0]?.topSites || []);
                setIsLoadingUsers(false);
            } catch (err) {
                if (err.name !== "AbortError") console.error(err);
            }
        };

        if (selectedStat === 0) {
            fetchTopSites();
        } else if (selectedStat === 1) {
            fetchTopUsers();
        }

        return () => abortController.abort();
    }, [userAnalyticsTab, selectedTime, portalFilter, selectedStat, dateRange]);

    useEffect(() => {
        if (userAnalyticsTab !== 1) return;
        const bothNull = !selectedStartDate && !selectedEndDate;
        const bothSet = selectedStartDate && selectedEndDate;
        if (!bothNull && !bothSet) return;
        const abortController = new AbortController();
        const now = new Date();
        const last8Years = new Date(); last8Years.setDate(now.getDate() - 2750);
        const fetchAllUsers = async () => {
            setIsLoadingUsers(true);
            try {
                const response = await getSiteAnalytics(apiDataRef.current, {
                    groupName: apiDataRef.current.companyName,
                    start_date: bothSet ? selectedStartDate : last8Years,
                    end_date: bothSet ? selectedEndDate : now,
                    analyticsType: "users",
                    hasPortalUsers: 2,
                }, dispatch, abortController.signal);
                setAllUserStats(response.data?.[0]?.allUsers || []);
                setIsLoadingUsers(false);
            } catch (err) {
                if (err.name !== "AbortError") console.error(err);
            }
        };
        fetchAllUsers();
        return () => abortController.abort();
    }, [userAnalyticsTab, selectedStartDate, selectedEndDate]);

    const handleUserClick = (userID, fullName) => {
        sessionStorage.setItem('dashboardSiteState', JSON.stringify({
            selectedStat,
            selectedTime,
            dateRange,
            portalFilter,
            userAnalyticsTab,
            selectedStartDate,
            selectedEndDate
        }));
        localStorage.setItem('clickedUserID', userID);
        localStorage.setItem('ClickedUserFullName', fullName);
        navigate('/Dashboard/Users/UserSites/');
    };

    const handleSiteClick = (DeviceUUID) => {
        sessionStorage.setItem('dashboardSiteState', JSON.stringify({
            selectedStat,
            selectedTime,
            dateRange,
            portalFilter,
            userAnalyticsTab,
            selectedStartDate,
            selectedEndDate
        }));
        dispatch(serviceActions.setDeviceUUID(DeviceUUID));
        setCookie("DeviceUUID", DeviceUUID, 1);
    };

    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T12:00:00.000Z`;
    };

    const handleOK = (value) => {
        if (value) {
            setDateRange([formatLocalDate(value[0]), formatLocalDate(value[1])]);
            setSelectedTime(null);
        } else {
            setDateRange(null);
        }
    };

    const disableDates = (date) => {
        return date < sixMonthsAgo || date > now;
    }

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 18) return "Good afternoon";
        return "Good evening";
    };

    const displayCompanyName = companyName === "EyeSpyFX" ? "Inniovate" : companyName;

    const myGroupPath = (() => {
        try { return JSON.parse(localStorage.getItem('groups') || '[]').find(g => g.name === companyName)?.groupPath || ''; }
        catch { return ''; }
    })();
    const showCamect = companyName === "EyeSpyFX" || myGroupPath.includes("RE:SURE");

    return (
        <div className={classes.pageContainer}>

            <div className={classes.greeting}>
                <h1 className={classes.greetingText}>{getGreeting()}{firstName ? `, ${firstName}` : ""}.</h1>
                <p className={classes.greetingSubtext}>{viewMode === 'portfolio' ? "Here's an overview of your full portfolio." : `Here's an overview of ${displayCompanyName || 'your group'}.`}</p>
                {/* <div className={classes.viewToggle}>
                    <button
                        className={`${classes.viewToggleBtn} ${viewMode === 'portfolio' ? classes.viewToggleBtnActive : ''}`}
                        onClick={() => setViewMode('portfolio')}
                    >
                        Full portfolio
                    </button>
                    <button
                        className={`${classes.viewToggleBtn} ${viewMode === 'myGroup' ? classes.viewToggleBtnActive : ''}`}
                        onClick={() => setViewMode('myGroup')}
                    >
                        {displayCompanyName || 'My group'}
                    </button>
                </div> */}
            </div>

            <div>
                <p className={classes.sectionTitle}>Portfolio overview</p>
                <div className={classes.portfolioGrid}>
                    {isLoading ? [...Array(8)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                        <StatCard title={"All sites"} value={stats.sitesCount} />
                        <StatCard title={"Active sites"} value={stats.activeCount} />
                        <StatCard title={"Inactive sites"} value={stats.inactiveCount} />
                        <StatCard title={"Sites on schedule"} value={stats.scheduleCount} hasSecondValue={true} value2={stats.activeScheduleCount} flipValues tooltip="Number of active sites with a permanent schedule within your group & sub groups" />
                        <StatCard title={"Active schedule only sites"} value={stats.scheduleOnlyCount} />
                        <StatCard title={"Live view enabled sites"} value={stats.liveViewSitesCount} />
                        <StatCard title={"Users"} value={stats.totalUserCount} hasSecondValue={true} value2={stats.activeTotalUserCount} flipValues tooltip="Number of users on an active site within your group and sub groups" />
                        <StatCard title={"Groups"} value={stats.accountsCount} />
                    </>}
                </div>
            </div>
<br/>
            {(hasLocks || hasKeyboxes || hasSensors || hasGateways) && <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="Device health" />}

            {(hasLocks || hasKeyboxes || hasSensors || hasGateways) && 
            <div className={classes.container}>
                {isLoading ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    {hasLocks && <StatCard title={"Padlocks"} value={stats.allLocksCount} hasSecondValue={true} value2={stats.allActiveLocksCount} flipValues tooltip="Number of padlocks linked to active sites within your group and sub groups" />}
                    {hasKeyboxes && <StatCard title={"Keyboxes"} value={stats.allKeyboxesCount} hasSecondValue={true} value2={stats.allActiveKeyboxesCount} flipValues tooltip="Number of keyboxes linked to active sites within your group and sub groups" />}
                    {hasSensors && <StatCard title={"Sensors"} value={stats.allSensors} hasSecondValue={true} value2={stats.allActiveSensors} flipValues tooltip="Number of sensors linked to active sites within your group and sub groups" />}
                </>}
            </div>}
        
            {(hasLocks || hasKeyboxes) && <div className={classes.chartsGrid}>
                {isLoadingRT ? [...Array(2)].map((_, i) => <SkeletonChart key={i} />) : RTStats && <>
                    {hasLocks && <DonutCard
                        title="Padlock battery health"
                        labels={["Critical (<5%)", "Warning (<20%)", "Healthy (>20%)"]}
                        values={[RTStats.criticalLocks, RTStats.warningLocks, RTStats.healthyLocks]}
                        colors={[C.critical, C.warning, C.healthy]}
                    />}
                    {hasKeyboxes && <DonutCard
                        title="Keybox battery health"
                        labels={["Critical (<5%)", "Warning (<20%)", "Healthy (>20%)"]}
                        values={[RTStats.criticalKeyboxes, RTStats.warningKeyboxes, RTStats.healthyKeyboxes]}
                        colors={[C.critical, C.warning, C.healthy]}
                    />}
                </>}
            </div>}

            <br/>
            <AnalyticsSectionHeader
                titles={["Site Stats", "User Stats"]}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                selectedStat={selectedStat}
                setSelectedStat={setSelectedStat}
                dateRange={dateRange}
                setDateRange={setDateRange}
                handleOK={handleOK}
                userAnalyticsTab={userAnalyticsTab}
                disableDates={disableDates}
            />

            {selectedStat === 0 && (
                isLoadingUsers ? (
                    <><SkeletonBarChart /><SkeletonTableCard /></>
                ) : topSitesStats.length > 0 ? (
                    <>
                        <AnalyticsBarCard
                            title="Top 10 sites by activity"
                            data={topSitesStats}
                            onItemClick={handleSiteClick}
                            selectedStat={selectedStat}
                        />
                        <AnalyticsTableCard
                            title="Top 50 sites by activity"
                            data={topSitesStats}
                            onItemClick={handleSiteClick}
                            selectedStat={selectedStat}
                        />
                    </>
                ) : (
                    <p style={{ textAlign: "center", color: "var(--text-label)", marginTop: "2rem" }}>No site activity data available for this time period.</p>
                )
            )}

            {selectedStat === 1 && (
                <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className={classes.userAnalyticsTabs}>
                            {["Top users", "User list"].map((tab, i) => (
                                <button key={tab} className={`${classes.userAnalyticsTab} ${userAnalyticsTab === i ? classes.userAnalyticsTabActive : ''}`} onClick={() => setUserAnalyticsTab(i)}>
                                    {tab}
                                </button>
                            ))}
                        </div>
                        {userAnalyticsTab === 0 && (
                            <SelectButton className={classes.filterToggle} value={portalFilter} onChange={(e) => setPortalFilter(e.value)} options={USER_PORTAL_OPTIONS} />
                        )}
                    </div>

                    {userAnalyticsTab === 0 && (
                        isLoadingUsers ? (
                            <><SkeletonBarChart /><SkeletonTableCard /></>
                        ) : topUserStats.length > 0 ? (
                            <>
                                <AnalyticsBarCard
                                    title="Top 10 users by output actions"
                                    data={topUserStats}
                                    onItemClick={handleUserClick}
                                    selectedStat={selectedStat}
                                />
                                {topUserStats.length > 10 && (
                                    <AnalyticsTableCard
                                        title="Top 50 users by output actions"
                                        data={topUserStats}
                                        onItemClick={handleUserClick}
                                        selectedStat={selectedStat}
                                    />
                                )}
                            </>
                        ) : (
                            <p style={{ textAlign: "center", color: "var(--text-label)", marginTop: "2rem" }}>No user activity data available for this time period.</p>
                        )
                    )}

                    {userAnalyticsTab === 1 && (
                        <>
                            <p className={classes.filterLabel} style={{marginTop: "12px"}}>Filter by creation date</p>
                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "16px", marginTop: "8px" }}>
                                    <Calendar value={selectedStartDate} onChange={(e) => setSelectedStartDate(e.value)} dateFormat="yy-mm-dd" placeholder="From" />
                                    <span style={{ color: "var(--text-label)" }}>—</span>
                                    <Calendar value={selectedEndDate} onChange={(e) => setSelectedEndDate(e.value)} dateFormat="yy-mm-dd" placeholder="To" />
                                    <span
                                        className={`${classes.clearText} ${(selectedStartDate || selectedEndDate) ? classes.clearTextActive : ''}`}
                                        onClick={() => {
                                            if (selectedStartDate || selectedEndDate) {
                                                setSelectedStartDate(null);
                                                setSelectedEndDate(null);
                                            }
                                        }}
                                    >
                                        Clear
                                    </span>
                                </div>
                                <ExportExcel 
                                    style={{height: "35px"}}
                                    excelData={formattedExportData}
                                    fileName={apiDataRef.current.companyName + " User List"}
                                    title={apiDataRef.current.companyName + " User List"}
                                    colWidths={
                                        [
                                            { wch: 40 },
                                            { wch: 40 },
                                            { wch: 10 },
                                            { wch: 10 },
                                            { wch: 10 },
                                        ]
                                    }   
                                />
                            </div>
                            {isLoadingUsers ? (
                                <div className={classes.chartCard}><div className={classes.skeletonBar} style={{ height: "300px", width: "100%" }} /></div>
                            ) : (
                                <DataTable value={allUserStats} paginator showGridlines rows={20} removableSort dataKey="userID" filters={userFilters} filterDisplay="row" globalFilterFields={['fullName', 'email']} emptyMessage="No users found.">
                                    <Column field="fullName" header="Name" filter filterPlaceholder="Search by name" style={{ minWidth: '12rem' }} body={(row) => <span className={classes.tableUser} onClick={() => handleUserClick(row.userID, row.fullName)}>{row.fullName}</span>} />
                                    <Column field="email" header="Email" filter filterPlaceholder="Search by email" style={{ minWidth: '12rem' }} />
                                    <Column field="totalSites" header="Site count" sortable style={{ minWidth: '4rem' }} />
                                    <Column field="created" header="Created" sortable style={{ minWidth: '10rem' }} />
                                    <Column field="lastLoggedIn" header="Last login" sortable style={{ minWidth: '10rem' }} />
                                </DataTable>
                            )}
                        </>
                    )}
                </>
            )}

<br/>
            {hasGateways && <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="Gateway health" />}
            {hasGateways && <div className={classes.container}>
                {isLoading ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    {hasGateways && <StatCard title={"Gateways"} value={stats.gatewayCount} hasSecondValue={false} value2={stats.activeGatewayCount} tooltip="Number of gateways linked to an active site within your group & sub groups" />}
                </>}
            </div>}
            {hasGateways && <div className={classes.chartsGrid}>
                {isLoadingRT ? [...Array(4)].map((_, i) => <SkeletonChart key={i} />) : RTStats && <>
                    <DonutCard
                        title="Connectivity status"
                        labels={["Online", "Missed 1 poll", "Offline", "Power down", "Unknown"]}
                        values={[RTStats.onlineGateways, RTStats.potentiallyStaleGateways, RTStats.staleGateways, RTStats.powerDownGateways, RTStats.unknownGateways]}
                        colors={[C.online, C.missed1poll, C.offline, C.powerDown, C.unknown]}
                    />
                    <DonutCard
                        title="Battery health"
                        labels={["Critical (<5%)", "Warning (<20%)", "Healthy (>20%)"]}
                        values={[RTStats.criticalGateways, RTStats.warningGateways, RTStats.healthyGateways]}
                        colors={[C.critical, C.warning, C.healthy]}
                    />
                    <DonutCard
                        title="Power source"
                        labels={["Mains", "Solar", "Battery"]}
                        values={[RTStats.mainsPoweredGateways, RTStats.solarPoweredGateways, RTStats.batteryPoweredGateways]}
                        colors={[C.mains, C.solar, C.battery]}
                    />
                    <DonutCard
                        title="Signal quality"
                        labels={["Poor", "Fair", "Good"]}
                        values={[RTStats.poorSignalGateways, RTStats.fairSignalGateways, RTStats.goodSignalGateways]}
                        colors={[C.poor, C.fair, C.good]}
                    />
                </>}
            </div>}
            <br/>
            {hasCCTV && <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="CCTV Licenses" />}
                {hasCCTV && <div className={classes.container}>
                    {isLoading ? [...Array(3)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                        <StatCard title={"Total CCTV licenses"} value={0} hasSecondValue={false} value2={stats.totalCCTVLicenses} flipValues tooltip="Total number of CCTV licenses in your group & subgroups" />
                        <StatCard title={"Used CCTV licenses"} value={stats.totalCCTVLicenses - stats.availableCCTVLicenses} hasSecondValue={false} value2={stats.totalCCTVLicenses - stats.availableCCTVLicenses} flipValues tooltip="Total number of used CCTV licenses in your group & subgroups" />
                        <StatCard title={"Available CCTV licenses"} value={stats.availableCCTVLicenses} hasSecondValue={false} value2={stats.availableCCTVLicenses} flipValues tooltip="Total number of available CCTV licenses in your group & subgroups" />
                        {stats.demoCCTVLicenses > 0 && <StatCard title={"Demo CCTV licenses"} value={stats.demoCCTVLicenses} hasSecondValue={false} value2={stats.demoCCTVLicenses} flipValues tooltip="Total number of Demo CCTV licenses in your group & subgroups" />}
                </>}
                </div>}
            <br/>
            {hasCCTV && <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="Linked CCTV Systems Connection Health" />}

            {hasCCTV && <div className={classes.container}>
                {isLoading ? [...Array(6)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    <StatCard title={"Total integrations"} value={stats.totalIntegrations} hasSecondValue={true} value2={stats.totalActiveIntegrations} flipValues tooltip="Number of active sites in your group & subgroups with a CCTV connection" />
                    <StatCard title={"AJAX"} value={stats.ajaxCount} hasSecondValue={true} value2={stats.activeAjaxCount} flipValues tooltip="Number of AJAX systems linked to an active site within your group and sub groups" />
                    <StatCard title={"Dahua"} value={stats.dahuaCount} hasSecondValue={true} value2={stats.activeDahuaCount} flipValues tooltip="Number of Dahua systems linked to an active site within your group and sub groups" />
                    <StatCard title={"Hikvision"} value={stats.hikvisionCount} hasSecondValue={true} value2={stats.activeHikvisionCount} flipValues tooltip="Number of Hikvision systems linked to an active site within your group and sub groups" />
                    <StatCard title={"AIBox"} value={stats.aiboxCount} hasSecondValue={true} value2={stats.activeAiboxCount} flipValues tooltip="Number of AIBox systems linked to an active site within your group and sub groups" />
                    {showCamect && <StatCard title={"Camect"} value={stats.camectCount} hasSecondValue={true} value2={stats.activeCamectCount} />}
                    {RTStats && <StatCard title={"Unstable CCTV connections"} value={stats.unstableCCTV} tooltip="Number of CCTV connections that have missed their last poll within your group & sub groups" />}
                </>}
            </div>}

            {hasCCTV && <CCTVAnalytics RTStats={RTStats} />}

            <br/>

             {hasIons && <>
                <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="iON health" />

                <div className={classes.siteHealthRow}>
                    <div className={classes.siteHealthDonut}>
                        {isLoadingRT ? <SkeletonChart /> : RTStats &&
                            <DonutCard
                                title="Online vs offline active iONs"
                                labels={["Active online", "Active offline"]}
                                values={[RTStats.onlineActiveiONs, RTStats.offlineActiveiONs]}
                                colors={[C.online, C.offline]}
                            />
                        }
                    </div>
                    <div className={classes.siteHealthStats}>
                        {(isLoading || isLoadingRT) ? [...Array(2)].map((_, i) => <SkeletonCard key={i} />) : stats && RTStats && <>
                            <StatCard title={"Total iON's"} value={stats.controllerCount} hasSecondValue={true} value2={stats.activeControllerCount} />
                            <StatCard title={"Total online iON's"} value={RTStats.onlineSites} hasSecondValue={true} value2={RTStats.onlineActiveiONs} onClick={() => navigate('/Dashboard/Offline')} />
                            <StatCard title={"Total offline iON's"} value={RTStats.offlineSites} hasSecondValue={true} value2={RTStats.offlineActiveiONs} onClick={() => navigate('/Dashboard/Offline')} />
                        </>}
                    </div>
                </div>
            </>}
<br/>
            <p className={classes.sectionTitle}>{viewMode === 'myGroup' ? `Billing (${displayCompanyName} only)` : 'Billing'}</p>

            <div className={classes.container} style={{ marginBottom: "24px" }}>
                {isLoading ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    <StatCard title={"Billable sites"} value={stats.billedSiteCount} />
                    {hasCCTV && <StatCard title={"CCTV sites"} value={stats.billableSitesWithCCTV} /> }
                    {hasLocks && <StatCard title={"Billable padlocks"} value={stats.billedLockCount} />}
                    {hasKeyboxes && <StatCard title={"Billable keyboxes"} value={stats.billedKeyboxCount} />}
                    {hasGateways && <StatCard title={"Billable gateways"} value={stats.billedGatewayCount} />}
                    {hasSensors && <StatCard title={"Billable sensors"} value={stats.billedSensorCount} />}
                </>}
            </div>

            {/* 
            {viewMode === 'myGroup' && <>
                <br/>
                <UserSectionHeader selectedTime={selectedTime} setSelectedTime={setSelectedTime} portalFilter={portalFilter} setPortalFilter={setPortalFilter} />
                <br/>

                <div className={classes.userAnalyticsTabs}>
                    {["Top users", "All users"].map((tab, i) => (
                        <button key={tab} className={`${classes.userAnalyticsTab} ${userAnalyticsTab === i ? classes.userAnalyticsTabActive : ''}`} onClick={() => setUserAnalyticsTab(i)}>
                            {tab}
                        </button>
                    ))}
                </div>

                {userAnalyticsTab === 0 && (
                    isLoadingUsers ? (
                        <div className={classes.chartCard}><div className={classes.skeletonBar} style={{ height: "300px", width: "100%" }} /></div>
                    ) : topUserStats.length > 0 ? (
                        <UserBarCard topUserData={topUserStats} onUserClick={handleUserClick} />
                    ) : (
                        <p style={{ textAlign: "center", color: "var(--text-label)", marginTop: "2rem" }}>No user activity data available for this time period.</p>
                    )
                )}

                {userAnalyticsTab === 1 && (
                    <>
                        <div style={{ display: "flex", gap: "16px", alignItems: "center", marginBottom: "16px" }}>
                            <Calendar value={selectedStartDate} onChange={(e) => setSelectedStartDate(e.value)} dateFormat="yy-mm-dd" placeholder="From" />
                            <span style={{ color: "var(--text-label)" }}>—</span>
                            <Calendar value={selectedEndDate} onChange={(e) => setSelectedEndDate(e.value)} dateFormat="yy-mm-dd" placeholder="To" />
                            {(selectedStartDate || selectedEndDate) && (
                                <button className={classes.timeButton} onClick={() => { setSelectedStartDate(null); setSelectedEndDate(null); }}>Clear</button>
                            )}
                        </div>
                        {isLoadingUsers ? (
                            <div className={classes.chartCard}><div className={classes.skeletonBar} style={{ height: "300px", width: "100%" }} /></div>
                        ) : (
                            <DataTable value={allUserStats} paginator showGridlines rows={20} removableSort dataKey="userID" filters={userFilters} filterDisplay="row" globalFilterFields={['fullName', 'email']} emptyMessage="No users found.">
                                <Column field="fullName" header="Name" filter filterPlaceholder="Search by name" style={{ minWidth: '12rem' }} body={(row) => <span className={classes.tableUser} onClick={() => handleUserClick(row.userID, row.fullName)}>{row.fullName}</span>} />
                                <Column field="email" header="Email" filter filterPlaceholder="Search by email" style={{ minWidth: '12rem' }} />
                                <Column field="totalSites" header="Site count" sortable style={{ minWidth: '4rem' }} />
                                <Column field="created" header="Created" sortable style={{ minWidth: '10rem' }} />
                                <Column field="lastLoggedIn" header="Last login" sortable style={{ minWidth: '10rem' }} />
                            </DataTable>
                        )}
                    </>
                )}
            </>}
            */}

        </div>
    );
};

export default Dashboard;

